import { creatorbase, TiktokUser, TiktokUserStatsCache } from '@round/api';
import { Skeleton, TableProps, getTableMetaHelper } from '@round/ui-kit';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import VirtualizedPostsTable from 'Modules/Plans/Posts/components/PostsTable/VirtualizedPostsTable';
import postsTableStyles from 'Modules/Plans/Posts/components/PostsTable/PostsTable.module.css';
import { usePublicReportTiktokPosts } from './hooks/usePublicReportTiktokPosts';
import { TikTokPublicReportCampaignTableRow } from '../../Campaigns/CampaignsTable/TikTokPublicReportCampaignTable';
import { BasePostTableMeta } from 'Modules/Plans/Posts/types';
import PublicPostsTableAccountCell from 'Modules/Plans/Posts/components/PostsTable/cells/PublicPostsTableAccountCell/PublicPostsTableAccountCell';
import PostStatsViewsFooterCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostStatsViewsFooterCell';
import PostsTableStatusCell, {
    StatusData,
} from 'Modules/Plans/Posts/components/PostsTable/cells/StatusCell/PostsTableStatusCell';
import { getTiktokEngagementRate, getTikTokTotalEngagement } from 'Modules/Plans/Posts/helpers';
import PostsTableEngagementRateFooterCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableEngagementRateFooterCell';
import PostsTableEngagementsFooterCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableEngagementsFooterCell';
import PostsTableUploadedDateCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableUploadedDateCell';
import PostStatsWithAverageCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsStatsWithAverageCell/PostsStatsWithAverageCell';
import PostStatsWithAverageFooterTotalCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsStatsWithAverageCell/PostStatsWithAverageFooterCell';
import { CampaignTableRow } from '../../Campaigns/CampaignsTable/types';
import { buildTiktokUserUrl } from 'helpers';
import { tooltipCopy } from 'Modules/Plans/helpers';
import { TooltipHeader } from '@round/whitelabel-ui';

export type TiktokPostsTableRow = creatorbase.PublicTiktokPost & {
    user: TiktokUser | null;
    userStatsCache: TiktokUserStatsCache | null;
    userImageUrl: string | null | undefined;
};

type Props = Pick<TableProps<TiktokPostsTableRow>, 'noDataLabel'> & {
    campaign: TikTokPublicReportCampaignTableRow;
};

type TableCellContext<TKey extends keyof TiktokPostsTableRow> = CellContext<
    TiktokPostsTableRow,
    TiktokPostsTableRow[TKey]
>;

type Meta = BasePostTableMeta<TiktokPostsTableRow> & {
    campaign: CampaignTableRow;
};

const getTableMeta = getTableMetaHelper<Meta>();

const PublicReportTiktokPostsTable = ({ campaign }: Props) => {
    const {
        posts,
        isPostsLoading,
        isPostsError,
        usersData,
        statsCacheData,
        imagesData,
        getIsAccountDataLoading,
        postsTotals,
        areTotalsLoading,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
    } = usePublicReportTiktokPosts(campaign.id);

    const columns = useMemo<ColumnDef<TiktokPostsTableRow, any>[]>(
        () => [
            {
                header: 'ID',
                meta: { className: postsTableStyles.idColumn },
                accessorKey: 'id',
                cell: ({ getValue, table }: TableCellContext<'id'>) => {
                    const { isLoading } = getTableMeta(table);

                    if (isLoading) {
                        return <Skeleton />;
                    }

                    return getValue().slice(-8);
                },
            },
            {
                header: 'Account',
                meta: { className: postsTableStyles.accountColumn },
                accessorFn: (row) => ({
                    image: row.userImageUrl,
                    title: row.user?.unique_id || '-',
                    followerCount: row.userStatsCache?.follower_count,
                    profileUrl: row.user?.unique_id ? buildTiktokUserUrl(row.user?.unique_id) : null,
                }),
                id: 'account',
                cell: PublicPostsTableAccountCell,
            },
            {
                header: 'Status',
                meta: { className: postsTableStyles.statusColumn },
                id: 'status',
                accessorFn: (original): StatusData => ({
                    postUrl: original.post_url,
                    platform: original.platform,
                    adCode: original.tiktok_details?.ad_code,
                    isReadOnly: true,
                }),
                cell: PostsTableStatusCell,
            },
            {
                header: 'Uploaded date',
                meta: { className: postsTableStyles.uploadedDateColumn },
                id: 'uploadedDate',
                cell: PostsTableUploadedDateCell,
            },
            {
                header: 'Views',
                meta: { className: postsTableStyles.statColumn },
                accessorFn: (row) => ({
                    value: row.view_count,
                    average: row.userStatsCache?.avg_post_views,
                }),
                id: 'views',
                cell: PostStatsWithAverageCell,
                footer: PostStatsViewsFooterCell,
            },
            {
                header: 'Total engagements',
                meta: { className: postsTableStyles.totalEngagementsColumn },
                id: 'totalEngagements',
                accessorFn: (row) => ({
                    value: getTikTokTotalEngagement(row),
                    average: row.userStatsCache?.avg_post_total_engagements,
                }),
                cell: PostStatsWithAverageCell,
                footer: PostsTableEngagementsFooterCell,
            },
            {
                header: () => <TooltipHeader header="ER" tooltipBody={tooltipCopy.engagementRate} />,
                meta: { className: postsTableStyles.statColumn },
                id: 'engagementRate',
                accessorFn: (row) => ({
                    value: getTiktokEngagementRate(row),
                    average: row.userStatsCache?.avg_engagement_rate,
                    formatValueToPercentage: true,
                }),
                cell: PostStatsWithAverageCell,
                footer: PostsTableEngagementRateFooterCell,
            },
            {
                header: 'Likes',
                meta: { className: postsTableStyles.statColumn },
                accessorFn: (row) => ({
                    value: row.tiktok_details?.like_count,
                    average: row.userStatsCache?.avg_post_likes,
                }),
                id: 'likes',
                cell: PostStatsWithAverageCell,
                footer: (context) => <PostStatsWithAverageFooterTotalCell {...context} totalsField="like_count" />,
            },
            {
                header: 'Shares',
                meta: { className: postsTableStyles.statColumn },
                accessorFn: (row) => ({
                    value: row.tiktok_details?.share_count,
                    average: row.userStatsCache?.avg_post_shares,
                }),
                id: 'shares',
                cell: PostStatsWithAverageCell,
                footer: (context) => <PostStatsWithAverageFooterTotalCell {...context} totalsField="share_count" />,
            },
            {
                header: 'Comments',
                meta: { className: postsTableStyles.statColumn },
                accessorFn: (row) => ({
                    value: row.tiktok_details?.comment_count,
                    average: row.userStatsCache?.avg_post_comments,
                }),
                id: 'comments',
                cell: PostStatsWithAverageCell,
                footer: (context) => <PostStatsWithAverageFooterTotalCell {...context} totalsField="comment_count" />,
            },
            {
                header: 'Saves',
                meta: { className: postsTableStyles.statColumn },
                accessorFn: (row) => ({
                    value: row.tiktok_details?.save_count,
                    average: row.userStatsCache?.avg_post_saves,
                }),
                id: 'saves',
                cell: PostStatsWithAverageCell,
                footer: (context) => <PostStatsWithAverageFooterTotalCell {...context} totalsField="save_count" />,
            },
        ],
        []
    );

    const meta: Meta = {
        isLoading: isPostsLoading,
        getIsAccountDataLoading,
        campaign,
        managedBy: campaign.managed_by,
        areApiTotalsEnabled: true,
        postsTotals,
        areTotalsLoading,
    };

    const rows: TiktokPostsTableRow[] = useMemo(
        () =>
            (posts as creatorbase.PublicPost[])
                .filter((p): p is creatorbase.PublicTiktokPost => p.platform === 'tiktok')
                .map((post) => {
                    const userId = post.tiktok_details?.account_id;
                    const user = userId ? usersData?.get(userId) ?? null : null;
                    const userStatsCache = userId ? statsCacheData?.get(userId) ?? null : null;
                    const userImage = userId ? imagesData?.get(userId) ?? null : null;

                    return {
                        ...post,
                        user,
                        userStatsCache,
                        userImageUrl: userImage?.avatar_thumb.cached_url || userImage?.avatar_thumb.original_url,
                    };
                }),
        [posts, usersData, statsCacheData, imagesData]
    );

    return (
        <VirtualizedPostsTable
            columns={columns}
            data={rows}
            meta={meta}
            isLoading={isPostsLoading}
            hasError={isPostsError}
            onEndReached={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
        />
    );
};

export default PublicReportTiktokPostsTable;
